import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertCircle,
  Eye,
  ImagePlus,
  Loader2,
  Paperclip,
  Truck,
  X,
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/shared/components/ui/button';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog';
import { Input } from '@/shared/components/ui/input';
import { Label } from '@/shared/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select';
import { Skeleton } from '@/shared/components/ui/skeleton';
import { Textarea } from '@/shared/components/ui/textarea';
import { cn } from '@/shared/lib/cn';
import { ddmmyyyy } from '@/features/ventas/components/Garantia';
import {
  confirmarDespacho,
  DESPACHO_FILE_TIPOS,
  prepareDespacho,
  type DespachoDetalleSummary,
  type DespachoFileTipo,
  type MantenimientoRow,
  type PropuestaUnidades,
} from '../api';
import {
  seleccionSugerida,
  totalSeleccion,
  UnidadesOrigenPicker,
  type SeleccionUnidades,
} from './UnidadesOrigenPicker';

/**
 * Confirmar Despacho — dispatch form. Three major UI blocks:
 *
 *   1. **Destinatario** (Recibe / Dirección / Observaciones) defaulted
 *      from the cliente's contact record.
 *   2. **Per-línea serials**: for each active detalle, render as many
 *      serial inputs as `cantidad`. Articles flagged LlevaNroSerie='SI'
 *      REQUIRE at least one serial per unit; others are optional.
 *   3. **Fotos de despacho**: multi-file upload (images + PDFs) with
 *      thumbnail preview. Stored under `Despachos/{ventaId}/` in Graph.
 *   4. **Origen del stock** (si está habilitado): por línea, de qué unidades
 *      (compra / importación / serie) sale. Viene preseleccionada la más
 *      antigua; elegir una unidad con serie fija el Nº de serie de la línea.
 *
 * Submit runs as a single backend transaction:
 * stock exit, DespachoHornos writes, venta header patch, BAA audit.
 * **Irreversible** once confirmed — matches PowerApps semantics.
 * The footer shows a warning about irreversibility.
 */

const MAX_PHOTOS = 20;
const MAX_PHOTO_SIZE = 10 * 1024 * 1024;

export function ConfirmarDespachoDialog({
  summary,
  onClose,
}: {
  summary: MantenimientoRow;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const prepareQ = useQuery({
    queryKey: ['mantenimiento', 'prepare-despacho', summary.id],
    queryFn: () => prepareDespacho(summary.id),
    staleTime: 10_000,
  });

  const [recibe, setRecibe] = useState('');
  const [direccion, setDireccion] = useState('');
  const [observaciones, setObservaciones] = useState('');
  // Each queued upload pairs a File with its tipo label, mirroring
  // the PowerApps `Collect_FotosDespacho` flow. The combobox-and-add
  // UI builds this list one entry at a time.
  const [photos, setPhotos] = useState<Array<{ file: File; tipo: DespachoFileTipo }>>([]);
  const [photoError, setPhotoError] = useState<string | null>(null);
  const [previewPhoto, setPreviewPhoto] = useState<File | null>(null);

  // Initialize form defaults once the prepare query resolves.
  useEffect(() => {
    if (!prepareQ.data) return;
    if (!recibe) setRecibe(prepareQ.data.suggestedRecibe);
    if (!direccion) setDireccion(prepareQ.data.suggestedDireccion);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prepareQ.data]);

  const detalles = prepareQ.data?.detalles ?? [];

  // Stock por unidades: propuestas por línea y la selección en curso.
  const propuestas = useMemo<PropuestaUnidades[]>(
    () => (prepareQ.data?.unidades?.habilitado ? prepareQ.data.unidades.propuestas : []),
    [prepareQ.data],
  );
  const [selecciones, setSelecciones] = useState<Record<string, SeleccionUnidades>>({});
  useEffect(() => {
    setSelecciones(Object.fromEntries(propuestas.map((p) => [p.detalleId, seleccionSugerida(p)])));
  }, [propuestas]);

  // Serials are now entered (and edited) via `NumerosSerieDespachoDialog`
  // in the maintenance module. Here we just read what's already on the
  // detalle (`currentSerie`) and display it; no input means the admin
  // hasn't filled it in yet, which blocks submit when required.
  const serialsByDetalle = useMemo(() => {
    const map: Record<string, string[]> = {};
    for (const d of detalles) {
      map[d.id] = (d.currentSerie ?? '')
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean);
      // Si todas las unidades elegidas tienen serie, esas series son las de la línea
      // (el servidor aplica la misma regla).
      const propuesta = propuestas.find((p) => p.detalleId === d.id);
      const elegidas = propuesta?.unidades.filter((u) => (selecciones[d.id]?.[u.id] ?? 0) > 0) ?? [];
      if (elegidas.length > 0 && elegidas.every((u) => u.nroSerie)) {
        map[d.id] = elegidas.map((u) => u.nroSerie as string);
      }
    }
    return map;
  }, [detalles, propuestas, selecciones]);

  // Nunca más unidades que las de la línea.
  const seleccionesOk = useMemo(
    () =>
      detalles.every((d) => {
        const sel = selecciones[d.id];
        return !sel || totalSeleccion(sel) <= d.cantidad + 0.0001;
      }),
    [detalles, selecciones],
  );

  const allSerialsOk = useMemo(() => {
    for (const d of detalles) {
      if (!d.llevaNroSerie) continue;
      const entries = serialsByDetalle[d.id] ?? [];
      const cantidad = Math.max(1, Math.ceil(d.cantidad));
      // Require every slot filled when the articulo requires serial.
      if (entries.length < cantidad) return false;
    }
    return true;
  }, [detalles, serialsByDetalle]);

  const canSubmit =
    prepareQ.data != null &&
    !prepareQ.data.despachado &&
    recibe.trim().length > 0 &&
    direccion.trim().length > 0 &&
    allSerialsOk &&
    seleccionesOk;

  const mutation = useMutation({
    mutationFn: () =>
      confirmarDespacho(summary.id, {
        recibe: recibe.trim(),
        direccion: direccion.trim(),
        observaciones: observaciones.trim(),
        detalles: Object.entries(serialsByDetalle).map(([detalleId, arr]) => {
          const picks = Object.entries(selecciones[detalleId] ?? {})
            .filter(([, cantidad]) => cantidad > 0)
            .map(([unidadId, cantidad]) => ({ unidadId: Number(unidadId), cantidad }));
          return { detalleId, serials: arr, ...(picks.length > 0 ? { unidades: picks } : {}) };
        }),
        photos,
      }),
    onSuccess: (r) => {
      if (r.outcome === 'confirmed') {
        const count = r.despachoRowIds.length;
        const articulos = `${count} artículo${count === 1 ? '' : 's'} despachado${count === 1 ? '' : 's'}`;
        if (r.unidades && r.unidades.sinOrigen > 0) {
          toast.info('Despacho sin origen asignado', {
            description: `${r.unidades.sinOrigen} un. se descontaron del stock sin una unidad de origen.`,
          });
        }
        if (r.email.sent) {
          toast.success('Despacho confirmado', {
            description: `${articulos}. Se descontó el stock y se envió el correo de confirmación.`,
          });
        } else {
          // The despacho still committed; warn the admin so they can
          // resend the email manually if needed.
          const reasonLabel =
            r.email.reason === 'no_recipient'
              ? 'el cliente no tiene correo cargado'
              : r.email.reason === 'pdf_fetch_failed'
                ? 'no se pudo descargar la guía adjunta'
                : 'falló el envío';
          toast.warning('Despacho confirmado, correo no enviado', {
            description: `${articulos}. Motivo: ${reasonLabel}.`,
          });
        }
        qc.invalidateQueries({ queryKey: ['mantenimiento'] });
        qc.invalidateQueries({ queryKey: ['inventario'] });
        qc.invalidateQueries({ queryKey: ['ventas'] });
        qc.invalidateQueries({ queryKey: ['home', 'operaciones'] });
        onClose();
      } else {
        toast.info('No aplicable', { description: r.message });
      }
    },
    onError: (err) =>
      toast.error('No se pudo confirmar el despacho', {
        description: err instanceof Error ? err.message : 'Error desconocido',
      }),
  });

  /** Adds a new (file, tipo) pair to the queue, validating size +
   *  mime + max-count. Returns true on success so the caller can
   *  reset the local form state. */
  const addPhoto = (file: File, tipo: DespachoFileTipo): boolean => {
    if (photos.length >= MAX_PHOTOS) {
      setPhotoError(`Máximo ${MAX_PHOTOS} fotos por despacho.`);
      return false;
    }
    if (file.size > MAX_PHOTO_SIZE) {
      setPhotoError(`"${file.name}" supera 10 MB.`);
      return false;
    }
    const ok = file.type.startsWith('image/') || file.type === 'application/pdf';
    if (!ok) {
      setPhotoError(`"${file.name}" no es imagen ni PDF.`);
      return false;
    }
    setPhotoError(null);
    setPhotos((prev) => [...prev, { file, tipo }]);
    return true;
  };

  const removePhoto = (idx: number) => {
    setPhotos((prev) => prev.filter((_, i) => i !== idx));
  };

  return (
    <Dialog open onOpenChange={(o) => !o && !mutation.isPending && onClose()}>
      <DialogContent
        className="max-w-3xl"
        processing={mutation.isPending && 'Confirmando despacho…'}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Truck className="h-4 w-4" aria-hidden />
            Confirmar Despacho · {summary.fields.Cliente_RO || 'Cliente'}
          </DialogTitle>
          <DialogDescription>
            Registra la entrega física del pedido, captura series por unidad
            y adjunta fotos. Esta acción descuenta el stock del inventario.
          </DialogDescription>
        </DialogHeader>
        <DialogBody className="space-y-5">
          {prepareQ.isPending ? (
            <PreloadSkeleton />
          ) : prepareQ.isError ? (
            <div className="rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm text-destructive">
              No se pudieron cargar los datos del despacho. Intentá nuevamente.
            </div>
          ) : prepareQ.data?.despachado ? (
            <div className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-800">
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
              <span>
                Esta venta ya fue despachada anteriormente. Cerrá el diálogo —
                no se pueden aplicar nuevos cambios desde acá.
              </span>
            </div>
          ) : (
            <>
              <RecipientSection
                recibe={recibe}
                onRecibeChange={setRecibe}
                direccion={direccion}
                onDireccionChange={setDireccion}
                observaciones={observaciones}
                onObservacionesChange={setObservaciones}
              />

              <DetallesSection
                detalles={detalles}
                propuestas={propuestas}
                selecciones={selecciones}
                onSeleccion={(detalleId, s) => setSelecciones((prev) => ({ ...prev, [detalleId]: s }))}
              />

              <PhotosSection
                photos={photos}
                error={photoError}
                onAdd={addPhoto}
                onRemove={removePhoto}
                onPreview={setPreviewPhoto}
              />

              {detalles
                .filter((d) => d.garantiaVencida)
                .map((d) => (
                  <div
                    key={d.id}
                    className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-800"
                  >
                    <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
                    <span>
                      <strong>Garantía vencida:</strong> la garantía de "{d.articulo}" venció el{' '}
                      {ddmmyyyy(d.garantiaVencida!)}. Podés despachar igual, pero el producto se
                      entrega con la garantía vencida.
                    </span>
                  </div>
                ))}

              <div className="flex items-start gap-2 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-800">
                <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
                <span>
                  <strong>Acción irreversible:</strong> se descontará el stock
                  de <em>Pendiente Despacho</em> y los artículos dejarán el
                  inventario. Si se cargan series erróneas, pueden editarse
                  desde la ficha del detalle más tarde.
                </span>
              </div>
            </>
          )}
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={mutation.isPending}>
            Cancelar
          </Button>
          <Button
            onClick={() => mutation.mutate()}
            disabled={!canSubmit || mutation.isPending}
            className="gap-2"
          >
            <Truck className="h-4 w-4" aria-hidden />
            Confirmar despacho
          </Button>
        </DialogFooter>
        {previewPhoto && (
          <PhotoPreviewDialog
            file={previewPhoto}
            onClose={() => setPreviewPhoto(null)}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Sub-sections
// ---------------------------------------------------------------------------

function RecipientSection({
  recibe,
  onRecibeChange,
  direccion,
  onDireccionChange,
  observaciones,
  onObservacionesChange,
}: {
  recibe: string;
  onRecibeChange: (v: string) => void;
  direccion: string;
  onDireccionChange: (v: string) => void;
  observaciones: string;
  onObservacionesChange: (v: string) => void;
}) {
  return (
    <section className="space-y-2">
      <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        Destinatario
      </h3>
      <div className="space-y-2 rounded-lg border bg-muted/20 p-3">
        <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
          <div className="space-y-1">
            <Label htmlFor="disp-recibe">
              Recibe <span className="text-destructive">*</span>
            </Label>
            <Input
              id="disp-recibe"
              value={recibe}
              onChange={(e) => onRecibeChange(e.target.value)}
              placeholder="Nombre y apellido"
              maxLength={200}
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="disp-direccion">
              Dirección completa <span className="text-destructive">*</span>
            </Label>
            <Input
              id="disp-direccion"
              value={direccion}
              onChange={(e) => onDireccionChange(e.target.value)}
              placeholder="Calle, número, ciudad, provincia"
              maxLength={500}
            />
          </div>
        </div>
        <div className="space-y-1">
          <Label htmlFor="disp-obs">
            Observaciones{' '}
            <span className="text-xs font-normal text-muted-foreground">· opcional</span>
          </Label>
          <Textarea
            id="disp-obs"
            rows={2}
            value={observaciones}
            onChange={(e) => onObservacionesChange(e.target.value)}
            placeholder="Indicaciones de entrega, horarios, contactos…"
            maxLength={2000}
          />
        </div>
      </div>
    </section>
  );
}

function DetallesSection({
  detalles,
  propuestas,
  selecciones,
  onSeleccion,
}: {
  detalles: DespachoDetalleSummary[];
  propuestas: PropuestaUnidades[];
  selecciones: Record<string, SeleccionUnidades>;
  onSeleccion: (detalleId: string, s: SeleccionUnidades) => void;
}) {
  if (detalles.length === 0) {
    return (
      <div className="rounded-md border border-dashed px-4 py-6 text-center text-sm text-muted-foreground">
        La venta no tiene detalles activos para despachar.
      </div>
    );
  }
  return (
    <section className="space-y-2">
      <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        Artículos a despachar ({detalles.length})
      </h3>
      <p className="text-xs text-muted-foreground">
        Los números de serie se cargan desde{' '}
        <span className="font-medium text-foreground">Num. de Serie | Despacho</span>.
        Acá se muestran como referencia.
      </p>
      <div className="space-y-2">
        {detalles.map((d) => {
          const propuesta = propuestas.find((p) => p.detalleId === d.id);
          return (
            <DetalleCard key={d.id} detalle={d}>
              {propuesta && (
                <UnidadesOrigenPicker
                  propuesta={propuesta}
                  cantidad={d.cantidad}
                  seleccion={selecciones[d.id] ?? {}}
                  onChange={(s) => onSeleccion(d.id, s)}
                />
              )}
            </DetalleCard>
          );
        })}
      </div>
    </section>
  );
}

function DetalleCard({
  detalle,
  children,
}: {
  detalle: DespachoDetalleSummary;
  children?: React.ReactNode;
}) {
  const units = Math.max(1, Math.ceil(detalle.cantidad));
  const serials = (detalle.currentSerie ?? '')
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
  // N° Despacho is one value per detalle (not per unit, unlike serie).
  const despacho = (detalle.currentDespacho ?? '').trim();
  return (
    <div className="rounded-md border bg-card p-3">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <div>
          <div className="font-medium text-foreground">
            {detalle.modelo || detalle.articulo || '—'}
          </div>
          {detalle.modelo && detalle.articulo && detalle.modelo !== detalle.articulo && (
            <div className="text-xs text-muted-foreground">{detalle.articulo}</div>
          )}
        </div>
        <span className="rounded-md bg-secondary px-2 py-0.5 text-xs font-semibold">
          {detalle.cantidad} {detalle.cantidad === 1 ? 'unidad' : 'unidades'}
        </span>
      </div>

      {/* Nº Despacho (per detalle) sits above the per-unit serie
          grid — a single value that applies to the whole line. For
          non-serie items (Accesorios etc.), both Nº Despacho and Nº
          Serie collapse to "—" since neither is tracked at the unit
          level for those cuentas contables. */}
      <div className="mt-3 grid grid-cols-1 gap-2 md:grid-cols-2">
        <DataField
          label="Nº Despacho"
          value={detalle.llevaNroSerie ? despacho : '—'}
          empty={detalle.llevaNroSerie && !despacho}
          missingLabel="Pendiente"
        />
        {detalle.llevaNroSerie && units === 1 && (
          <DataField
            label="Nº Serie"
            value={serials[0] ?? ''}
            empty={!serials[0]}
            missingLabel="Pendiente"
          />
        )}
      </div>

      {/* When the article carries serie AND has more than one unit,
          render a row of per-unit serie slots beneath the line. */}
      {detalle.llevaNroSerie && units > 1 && (
        <div className="mt-2 grid grid-cols-1 gap-2 md:grid-cols-2">
          {Array.from({ length: units }).map((_, i) => {
            const value = serials[i] ?? '';
            return (
              <DataField
                key={i}
                label={`Nº Serie · unidad ${i + 1}`}
                value={value}
                empty={!value}
                missingLabel="Pendiente"
              />
            );
          })}
        </div>
      )}
      {children}
    </div>
  );
}

function DataField({
  label,
  value,
  empty,
  missingLabel,
}: {
  label: string;
  value: string;
  empty: boolean;
  missingLabel: string;
}) {
  return (
    <div
      className={cn(
        'rounded-md border px-2.5 py-1.5',
        empty
          ? 'border-destructive/40 bg-destructive/5'
          : 'border-border bg-muted/30',
      )}
    >
      <p className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
        {label}
      </p>
      <p
        className={cn(
          'truncate text-sm',
          empty ? 'text-destructive' : 'text-foreground',
        )}
        title={value || undefined}
      >
        {value || missingLabel}
      </p>
    </div>
  );
}

function PhotosSection({
  photos,
  error,
  onAdd,
  onRemove,
  onPreview,
}: {
  photos: Array<{ file: File; tipo: DespachoFileTipo }>;
  error: string | null;
  onAdd: (file: File, tipo: DespachoFileTipo) => boolean;
  onRemove: (idx: number) => void;
  onPreview: (f: File) => void;
}) {
  // Local staging: pick a tipo + a file, then click Agregar. Mirrors
  // the legacy PowerApps `Cbox_TipoFotoDespacho_DM` + AddMedia flow
  // — the file isn't pushed to the upload queue until the admin
  // explicitly confirms the (file, tipo) pair.
  const [tipo, setTipo] = useState<DespachoFileTipo | ''>('');
  const [file, setFile] = useState<File | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const canAdd = Boolean(tipo) && file !== null;

  const handleAdd = () => {
    if (!canAdd || !tipo || !file) return;
    if (onAdd(file, tipo)) {
      setTipo('');
      setFile(null);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  return (
    <section className="space-y-2">
      <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        Fotos / documentos ({photos.length}/{MAX_PHOTOS})
      </h3>
      <div className="space-y-3 rounded-lg border bg-muted/20 p-3">
        <p className="text-xs text-muted-foreground">
          Elegí el <strong>tipo</strong>, adjuntá el archivo y presioná{' '}
          <strong>Agregar</strong>. Podés cargar varios documentos
          repitiendo el flujo. Se requiere al menos uno.
        </p>
        <div className="grid grid-cols-1 gap-2 md:grid-cols-[minmax(8rem,1fr),minmax(10rem,2fr),auto]">
          <div className="space-y-1">
            <Label className="text-[11px] uppercase tracking-wide text-muted-foreground">
              Tipo
            </Label>
            <Select
              value={tipo || undefined}
              onValueChange={(v) => setTipo(v as DespachoFileTipo)}
            >
              <SelectTrigger className="h-9 w-full">
                <SelectValue placeholder="Seleccionar tipo…" />
              </SelectTrigger>
              <SelectContent>
                {DESPACHO_FILE_TIPOS.map((t) => (
                  <SelectItem key={t} value={t}>
                    {t}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="min-w-0 space-y-1">
            <Label className="text-[11px] uppercase tracking-wide text-muted-foreground">
              Archivo
            </Label>
            {/* Hidden file input — used by both the empty-state
                "Adjuntar archivo" button and the filled-state row's
                click-to-replace behavior is intentionally NOT wired
                so the eye/trash buttons stay obvious. */}
            <input
              ref={inputRef}
              type="file"
              accept="image/*,application/pdf"
              className="hidden"
              onChange={(e) => {
                const picked = e.target.files?.[0] ?? null;
                setFile(picked);
                e.currentTarget.value = '';
              }}
            />
            {file ? (
              <div className="flex h-9 min-w-0 items-center gap-1 rounded-md border bg-background px-2 text-sm">
                <ImagePlus className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
                <span className="min-w-0 flex-1 truncate text-foreground" title={file.name}>
                  {file.name}
                </span>
                <button
                  type="button"
                  onClick={() => onPreview(file)}
                  aria-label="Vista previa"
                  title="Vista previa"
                  className="shrink-0 rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
                >
                  <Eye className="h-3.5 w-3.5" aria-hidden />
                </button>
                <button
                  type="button"
                  onClick={() => setFile(null)}
                  aria-label="Quitar archivo"
                  title="Quitar archivo"
                  className="shrink-0 rounded p-1 text-muted-foreground hover:bg-muted hover:text-destructive"
                >
                  <X className="h-3.5 w-3.5" aria-hidden />
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => inputRef.current?.click()}
                className="inline-flex h-9 w-full items-center gap-1.5 rounded-md border bg-background px-3 text-sm text-muted-foreground transition-colors hover:bg-secondary"
              >
                <ImagePlus className="h-4 w-4 shrink-0" aria-hidden />
                <span>Adjuntar archivo</span>
              </button>
            )}
          </div>
          <div className="flex items-end">
            <Button
              type="button"
              onClick={handleAdd}
              disabled={!canAdd}
              className="h-9 w-full md:w-auto"
            >
              Agregar
            </Button>
          </div>
        </div>
        {error && (
          <div className="text-xs text-destructive">{error}</div>
        )}
        {photos.length > 0 && (
          <div className="flex flex-wrap gap-2 border-t border-dashed pt-2">
            {photos.map((p, i) => (
              <PhotoChip
                key={i}
                file={p.file}
                tipo={p.tipo}
                onPreview={() => onPreview(p.file)}
                onRemove={() => onRemove(i)}
              />
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

function PhotoChip({
  file,
  tipo,
  onPreview,
  onRemove,
}: {
  file: File;
  tipo: DespachoFileTipo;
  onPreview: () => void;
  onRemove: () => void;
}) {
  return (
    <div className="flex items-center gap-1.5 rounded-md border bg-background px-2 py-1 text-[11px] text-muted-foreground">
      <span className="rounded bg-secondary px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-foreground">
        {tipo}
      </span>
      <Paperclip className="h-3 w-3" aria-hidden />
      <button
        type="button"
        onClick={onPreview}
        title="Vista previa"
        className="max-w-[180px] truncate text-left text-foreground hover:underline"
      >
        {file.name}
      </button>
      <span className="text-[10px] opacity-70">
        {Math.round(file.size / 1024)} KB
      </span>
      <button
        type="button"
        onClick={onPreview}
        aria-label={`Vista previa ${file.name}`}
        title="Vista previa"
        className="rounded p-0.5 hover:bg-muted"
      >
        <Eye className="h-3 w-3" aria-hidden />
      </button>
      <button
        type="button"
        onClick={onRemove}
        aria-label={`Quitar ${file.name}`}
        title="Quitar"
        className="rounded p-0.5 hover:bg-muted"
      >
        <X className="h-3 w-3" aria-hidden />
      </button>
    </div>
  );
}

/**
 * Inline preview for a staged file. Same blob-URL pattern used in the
 * pago dialogs — created on mount, revoked on unmount. Renders images
 * inline and PDFs in an iframe.
 */
function PhotoPreviewDialog({
  file,
  onClose,
}: {
  file: File;
  onClose: () => void;
}) {
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  useEffect(() => {
    const url = URL.createObjectURL(file);
    setBlobUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);
  const isImage = file.type.startsWith('image/');
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle className="truncate">{file.name}</DialogTitle>
        </DialogHeader>
        <DialogBody>
          {!blobUrl ? (
            <div className="flex h-64 items-center justify-center text-muted-foreground">
              <Loader2 className="h-5 w-5 animate-spin" aria-hidden />
            </div>
          ) : isImage ? (
            <img
              src={blobUrl}
              alt={file.name}
              className="mx-auto max-h-[70vh] w-auto rounded-lg"
            />
          ) : (
            <iframe
              src={blobUrl}
              title={file.name}
              className="h-[70vh] w-full rounded-lg border"
            />
          )}
        </DialogBody>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose} className="gap-2">
            <X className="h-4 w-4" aria-hidden />
            Cerrar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function PreloadSkeleton() {
  return (
    <div className="space-y-3">
      <Skeleton className="h-24 w-full" />
      <Skeleton className="h-32 w-full" />
      <Skeleton className="h-16 w-full" />
    </div>
  );
}
